import {useDispatch, useSelector} from 'react-redux';
import {assessmentsConfig, externalConfigs, saveExternalConfigs} from '../../../../store/reducers/externalConfig';
import AgyleToggleButtons from '../../../../components/ui/buttons/agyleToggleButtons';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {PATIENT_CONTEXT_TYPE} from '../../../../util/patientContextUtil';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AddIcon from '@mui/icons-material/Add';
import {setSnackbar} from '../../../../store/reducers/globals';
import _ from 'lodash';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';
import useOpenState from '../../../../hooks/useOpenState';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import {FormLabel, IconButton} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {agyleAssessmentList} from '../../../../store/reducers/dicts';
import ControlSpecialty from '../../../../controls/agyle/specialty';
import InformationBox from '../../../../components/other/informationBox';
import AssessmentListInPatientBox from './assessmentListInPatientBox';

const useStyles = makeStyles((theme) => ({
    toggleRoot: {
        color: theme.palette.mode === 'dark' ? '#222222' : '#f3f3f4',
        backgroundColor: theme.palette.mode === 'dark' ? '#313233' : '#eff1f3',
        display: 'inline-block',
        width: '100%',
        padding: '5px !important',
    },
    toggleLabel: {
        color: theme.palette.mode === 'dark' ? '#ACAFB3' : '#222222',
        height: '25px !important',
        fontSize: 12,
        '&.icon': {
            fontSize: 10,
        },
        '&.Mui-disabled': {
            border: 'none',
        },
    },
    toggleSelected: {
        color: theme.palette.mode === 'dark' ? '#222222 !important' : '#ffffff !important',
        fontWeight: 700,
        backgroundColor: theme.palette.mode === 'dark' ? '#ffffff !important' : '#626466 !important',
    },
    table: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 10,
    },
    tableContainer: {
        width: '100%',
        marginTop: 10,
        padding: 5,
    },
    assessment: {
        backgroundColor: theme.palette.inPatient.inputBackground,
        borderRadius: 6,
        margin: '5px 0',
        padding: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
}));

const AssessmentConfiguration = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const assessmentList = useSelector(agyleAssessmentList);
    const cfgs = useSelector(externalConfigs);
    const assCfg = useSelector(assessmentsConfig);
    const [externalConfig, setExternalConfig] = useState(null);

    const [currentConfig, setCurrentConfig] = useState(null);
    const [toggle, setToggle] = useState(PATIENT_CONTEXT_TYPE.ED);
    const isIP = toggle === PATIENT_CONTEXT_TYPE.INPATIENT;

    const [isAssessmentOpen, openAssessment, closeAssessment] = useOpenState(false, true);
    const [isSpecOpen, openSpec, closeSpec] = useOpenState(false, true);

    const tempType = useRef(null);
    const tempSpec = useRef(null);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [selectedSpec, setSelectedSpec] = useState(null);

    useEffect(() => {
        const find = _.find(cfgs, cfg => cfg.configType === 'assessments');
        if (find) {
            setExternalConfig(find);
        }
    }, [cfgs]);

    useEffect(() => {
        setCurrentConfig(assCfg?.[toggle]);
    }, [assCfg, toggle]);

    const handleSaveAssessmentList = () => {
        const clone = _.cloneDeep(assCfg);
        clone[toggle] = currentConfig;

        const objToSave = {
            __id: externalConfig?.__id || null,
            description: 'Agyle Assessment list',
            configType: 'assessments',
            active: true,
            value: clone,
        };
        dispatch(saveExternalConfigs(objToSave));
    };

    const handleCloseAssessment = () => {
        closeAssessment();
        setSelectedAssessment(null);
        tempType.current = null;
        tempSpec.current = null;
    };

    const handleCloseSpecialty = () => {
        closeSpec();
        setSelectedSpec(null);
    };

    const handleAddAssessment = () => {
        if (isIP) {
            const specAssessments = _.find(currentConfig[tempType.current], spec => spec.id === tempSpec.current);
            const isExists = _.find(specAssessments.assessmentList, a => a === selectedAssessment?.code);
            if (isExists) {
                dispatch(setSnackbar({type: 'warning', message: t('Assessment is already on the list')}));
            } else {
                const clone = _.cloneDeep(currentConfig);
                const specAssessments = _.find(clone[tempType.current], spec => spec.id === tempSpec.current);
                specAssessments.assessmentList.push(selectedAssessment.code);
                setCurrentConfig(clone);
                handleCloseAssessment();
            }
        } else {
            const isExists = _.find(currentConfig[tempType.current], a => a === selectedAssessment?.code);
            if (isExists) {
                dispatch(setSnackbar({type: 'warning', message: t('Assessment is already on the list')}));
            } else {
                const clone = _.cloneDeep(currentConfig);
                clone[tempType.current].push(selectedAssessment.code);
                setCurrentConfig(clone);
                handleCloseAssessment();
            }
        }
    };

    const handleAddSpecGroup = () => {
        const isExists = _.find(currentConfig.mandatory, a => a?.id === selectedSpec?.__code);
        if (isExists) {
            dispatch(setSnackbar({type: 'warning', message: t('Specialty is already on the list')}));
        } else {
            const clone = _.cloneDeep(currentConfig);
            const obj = {
                id: selectedSpec?.__code,
                specGroup: selectedSpec,
                assessmentList: [],
            };
            clone.mandatory.push(_.cloneDeep(obj));
            clone.suggested.push(_.cloneDeep(obj));
            setCurrentConfig(clone);
            handleCloseSpecialty();
        }
    };

    const handleRemove = (ass, type) => {
        let clone = _.cloneDeep(currentConfig);
        clone[type] = _.filter(clone[type], a => a !== ass);
        setCurrentConfig(clone);
    };

    const renderAssessment = (assessment, type) => {
        const find = _.find(assessmentList, a => a.code === assessment);
        if (!find) return null;

        return (
            <div key={find?.__id || Math.random()} className={classes.assessment}>
                {find?.description || 'UNK'}

                <IconButton
                    size="small"
                    onClick={() => handleRemove(assessment, type)}
                >
                    <DeleteForeverIcon fontSize="small"/>
                </IconButton>
            </div>
        );
    };

    const renderAssessmentIP = (specGroup, type) => {
        return (
            <div className={classes.tableContainer}>
                <FormLabel style={{fontWeight: 'bold', textAlign: 'center'}}>
                    {_.startCase(type)}
                </FormLabel>

                <AgyleButton
                    colour="primary"
                    onClick={() => {
                        tempType.current = type;
                        tempSpec.current = specGroup?.id;
                        openAssessment();
                    }}
                    startIcon={<AddIcon/>}
                    width="100%"
                    size="small"
                >
                    {t('Add new assessment')}
                </AgyleButton>

                {specGroup?.assessmentList?.map(ass => (
                    renderAssessment(ass, type)
                ))}
            </div>
        );
    };

    const renderTable = useCallback((type) => {
        if (isIP) {
            if (_.isEmpty(currentConfig?.[type])) return (
                <InformationBox type="info">
                    {t('There is no data to display.')}
                </InformationBox>
            );

            if (!currentConfig?.[type]?.map) return null;

            return (
                <div className={classes.tableContainer} style={{display: 'flex', flexDirection: 'column'}}>
                    {currentConfig[type].map(ass => (
                        <AssessmentListInPatientBox assessment={ass} key={ass.id}>
                            {renderAssessmentIP(ass, type)}
                        </AssessmentListInPatientBox>
                    ))}
                </div>
            );
        } else {
            if (!currentConfig?.[type]?.map) return null;

            return (
                <div className={classes.tableContainer}>
                    <FormLabel style={{fontWeight: 'bold', textAlign: 'center'}}>
                        {_.startCase(type)}
                    </FormLabel>

                    <AgyleButton
                        colour="primary"
                        onClick={() => {
                            tempType.current = type;
                            openAssessment();
                        }}
                        startIcon={<AddIcon/>}
                        width="100%"
                        size="small"
                    >
                        {t('Add new assessment')}
                    </AgyleButton>

                    {currentConfig[type].map(ass => (
                        renderAssessment(ass, type)
                    ))}
                </div>
            );
        }
    }, [currentConfig]);

    const handleListOfAvailableAssessment = () => {
        if (tempType.current) {
            let currentElements = currentConfig[tempType.current] || [];
            if (tempSpec.current) {
                const find = _.find(currentElements, {id: tempSpec.current});
                currentElements = find?.assessmentList ?? [];
            }
            return _.filter(assessmentList, ass => !currentElements.includes(ass.code));
        }
        return [];
    };

    return (
        <>
            <AgyleToggleButtons
                classNameRoot={classes.toggleRoot}
                classNameLabel={classes.toggleLabel}
                classNameSelected={classes.toggleSelected}
                onChange={(e) => {
                    if (e) {
                        setToggle(e);
                    }
                }}
                value={toggle}
                size="small"
                options={[
                    {value: PATIENT_CONTEXT_TYPE.ED, label: t('Emergency Department')},
                    {value: PATIENT_CONTEXT_TYPE.SDEC, label: t('SDEC')},
                    {value: PATIENT_CONTEXT_TYPE.EDAU, label: t('EDAU')},
                    {value: PATIENT_CONTEXT_TYPE.INPATIENT, label: t('InPatient')},
                ]}
                minWidth={100}
                fullWidth
            />

            <AgyleButton
                variant="contained"
                width="100%"
                style={{marginTop: 10}}
                startIcon={<SaveIcon/>}
                onClick={handleSaveAssessmentList}
            >
                {t('Save assessment list')}
            </AgyleButton>

            {isIP &&
                <AgyleButton
                    variant="contained"
                    width="100%"
                    style={{marginTop: 10}}
                    onClick={openSpec}
                >
                    {t('Add specialty')}
                </AgyleButton>
            }

            <div className={classes.table}>
                {renderTable('mandatory')}
                {renderTable('suggested')}
            </div>

            <MiniFormDialog
                title={t('Add assessment')}
                open={isAssessmentOpen}
                onClose={handleCloseAssessment}
                onSave={handleAddAssessment}
                width="sm"
                disableSubmit={!selectedAssessment}
            >
                <AgyleDictionaryField
                    label={t('Form')}
                    options={handleListOfAvailableAssessment()}
                    onChange={(_e, val) => setSelectedAssessment(val)}
                    value={selectedAssessment}
                    noBorder={false}
                    withNewBackground
                    size="large"
                    getOptionLabel={option => option.description}
                />
            </MiniFormDialog>

            <MiniFormDialog
                title={t('Add specialty group')}
                open={isSpecOpen}
                onClose={handleCloseSpecialty}
                onSave={handleAddSpecGroup}
                width="sm"
                disableSubmit={!selectedSpec}
            >
                <ControlSpecialty
                    label={t('Select specialty')}
                    onChange={setSelectedSpec}
                    value={selectedSpec}
                    noBorder={false}
                    withNewBackground
                    size="large"
                    groupBy={opt => opt.division}
                />
            </MiniFormDialog>
        </>
    );
};

export default AssessmentConfiguration;